/**
 * The Library of Memories — the full story of Relikara, told across 100 codices.
 *
 * Each helix line on the Helix of Restoration is one CODEX. A codex holds 6–9
 * distinct memories; recover EVERY memory in a codex and that helix line lights
 * (and forges its Legendary Memory Relic). The whole library is ~600–900 unique
 * memories, revealed by depth: shallow floors surrender the early codices, the
 * deepest give up the last.
 *
 * The library is generated deterministically from tiered lore banks, so a memory
 * id ("m12-3") always maps to the same text — saves reference memories by id.
 * Rewrite the banks below to author real lore; ids stay stable.
 */
import { RARITY } from "./constants.js";

export const CODEX_COUNT = 100;           // one per helix line
const TIER_SIZE = 20;                     // 5 tiers × 20 codices

// small deterministic hash → 32-bit uint (so generation is stable across loads)
function hash(a, b) {
  let h = 2166136261 ^ (a * 374761393) ^ (b * 668265263);
  h = Math.imul(h ^ (h >>> 15), 2246822519);
  h = Math.imul(h ^ (h >>> 13), 3266489917);
  return (h ^ (h >>> 16)) >>> 0;
}
const pick = (arr, seed) => arr[seed % arr.length];

// ---- Five story tiers. Openers + cores + closes combine into memory prose. ----
const TIERS = [
  {
    name: "Foundation", color: "#4aa3ff",
    codexA: ["The Sealing", "The Quiet Age", "Warden's", "The First", "Before the", "The Sleeping", "The Architects'", "The Gathered", "The Unbroken", "The Keeper's"],
    codexB: ["Vigil", "Hall", "Covenant", "Dawn", "Threshold", "Foundation", "Silence", "Cradle", "Ledger", "Vow"],
    titleA: ["Sealed", "Quiet", "First", "Sleeping", "Warden's", "Elder", "Patient", "Hollow", "Gathered", "Unspoken"],
    titleB: ["Light", "Oath", "Threshold", "Hour", "Ledger", "Key", "Watch", "Cradle", "Silence", "Root"],
    openers: [
      "In the age before the Fracture,", "When the Keeper still watched,", "Long before the first door opened,",
      "In the world's patient youth,", "Before any seeker was born to sorrow,", "When Relikara only dreamed,",
      "In the quiet that had no name,", "Before the collection ever stirred,", "When the halls were newly sung,",
      "In the years the Architects kept faith,", "Before memory learned to wander,", "At the founding of the Vault,",
    ],
    cores: [
      "the Architects gathered every dangerous wonder into one sleeping hall",
      "the Keeper counted the relics as a shepherd counts a flock",
      "no artifact was stolen, only kept — for keeping was the only mercy",
      "the deepest doors were sealed with names no living tongue remembers",
      "each relic was laid to rest with the memory it could not release",
      "the Vault was made larger on the inside than the world without",
      "the Architects taught the halls to remember, so nothing would be lost",
      "a lattice of wards was woven to hold the collection in gentle sleep",
      "the first Keeper took the oath that would outlast every kingdom",
      "the founders wrote that every civilization is undone by what it makes",
      "the relics were sung to sleep, one by one, across a thousand years",
      "the Vault's heart was set beating so the whole world need not",
      "they built Eden before the danger, as if they already knew",
      "the balance was struck: nothing enters the world, nothing leaves the dark",
    ],
    closes: [
      "— and they called it mercy.", "so the balance held.", "and Relikara slept, unbroken.",
      "and no relic ever wandered.", "for a while, it was enough.", "and the oath held fast.",
      "and the world was spared its own inventions.", "— a kindness the world would forget.",
      "and the silence kept its promise.", "so the wonder did no harm.",
    ],
  },
  {
    name: "Ascension", color: "#5fd35f",
    codexA: ["The Leaking", "The Rising", "The Greedy", "The First Seekers", "The Opened", "The Bright", "The Crowned", "The Bargained", "The Wandering", "The Golden"],
    codexB: ["Doors", "Kingdoms", "Hoard", "Descent", "Crown", "Age", "Wound", "Debt", "Relics", "Fever"],
    titleA: ["Leaking", "Crowned", "Greedy", "Bright", "Bargained", "Rising", "Golden", "Opened", "Hungry", "Borrowed"],
    titleB: ["Door", "Crown", "Fever", "Coin", "Debt", "Throne", "Wound", "Market", "Banner", "Vow"],
    openers: [
      "After the doors first opened,", "As the relics spilled outward,", "When kingdoms learned the Vault's name,",
      "In the fever of the early years,", "When the first seekers returned rich,", "As word of the depths spread,",
      "When crowns were bought with borrowed wonder,", "In the age of open gates,", "As the world grew drunk on relics,",
      "When every court craved a shard,", "As the wardings failed one by one,", "In the century of the golden fever,",
    ],
    cores: [
      "kingdoms rose overnight on treasures they were never meant to hold",
      "merchants sold memories as if they were mere gold",
      "the first seekers descended and came back changed, or not at all",
      "a relic could raise a beggar to a king within a single season",
      "whole cities vanished the night their borrowed wonder failed them",
      "Eden opened its doors to the ragged and the ruined alike",
      "greed learned the shape of the Vault and could not look away",
      "each opened door let something out that had waited a long, patient age",
      "the wardings frayed, and the balance the Architects struck began to tip",
      "heroes carved their names into the shallow halls and thought it glory",
      "the relics remembered their old wars and whispered them to new hands",
      "kingdoms warred over shards that remembered older, crueler kings",
      "the descent became a trade, and the trade became a faith",
      "power flowed up from the dark, and the world drank it like wine",
    ],
    closes: [
      "and no one asked the cost.", "and the cost came due regardless.", "— glory, and its long shadow.",
      "and the Vault only deepened.", "so the fever spread.", "and few returned unchanged.",
      "and the balance forgot itself.", "— the price written in a later hand.",
      "and the dark took its measure.", "so the age burned bright and brief.",
    ],
  },
  {
    name: "Communion", color: "#c77dff",
    codexA: ["Eden's", "The Descent", "The Awakening", "The Companion", "The Kept Fire", "The Seeker's", "The Bonded", "The Remembering", "The Sheltered", "The Named"],
    codexB: ["Hearth", "Rite", "Song", "Bond", "Vigil", "Creed", "Pact", "Relic", "Road", "Flame"],
    titleA: ["Kept", "Bonded", "Waking", "Sheltered", "Named", "Shared", "Warm", "Faithful", "Remembered", "Quiet"],
    titleB: ["Fire", "Bond", "Song", "Hearth", "Pact", "Road", "Vigil", "Name", "Ember", "Creed"],
    openers: [
      "In the long middle years,", "As the seekers made a culture of the dark,", "When Eden became home,",
      "As relics began to wake to their bearers,", "When the road between floors grew worn,", "In the age of the kept fire,",
      "As companions followed heroes into the deep,", "When the Vault learned a seeker's name,", "As the descent became a life,",
      "When memory answered memory,", "In the seasons of the shared hearth,", "As the deep and the sheltered learned each other,",
    ],
    cores: [
      "Eden kept every fire burning and every bed warm for the lost",
      "a sword woke to its wielder and remembered every battle it survived",
      "seekers learned that power was not taken but awakened, memory by memory",
      "strange companions walked out of the dark to stand beside the living",
      "the merchant somehow knew each traveler's name before they spoke it",
      "the deepest relics began to answer, and some began to want",
      "heroes bonded to relics that had loved older hands, and felt their grief",
      "the Vault started to remember the ones who came again and again",
      "a shield recalled every life it had saved and grew heavy with them",
      "the road between floors filled with songs the descent had made its own",
      "companions were fed, leveled, and mourned like any comrade of flesh",
      "the sheltered places asked nothing and gave everything, and no one knew why",
      "memory called to memory across the dark, and answered",
      "the seekers built a faith from firelight and the courage to go down again",
    ],
    closes: [
      "and the dark felt, for a moment, like home.", "and the fire never asked for thanks.",
      "— a warmth against the deepening cold.", "and the relic's will grew.",
      "so the bond was sealed.", "and the Vault remembered.", "and the song went down with them.",
      "— kinship, forged in the deep.", "and no one wished to leave.", "so the living were not alone.",
    ],
  },
  {
    name: "Radiance", color: "#f0a63c",
    codexA: ["The Forgotten", "The Bottomless", "The Looping", "The Elder Dark", "The Impossible", "The Unwritten", "The Deep Truth", "The Shifting", "The Nameless", "The Ageless"],
    codexB: ["Halls", "Depth", "Hour", "Choir", "Door", "Wound", "Mirror", "Throne", "Echo", "Vault"],
    titleA: ["Forgotten", "Bottomless", "Looping", "Elder", "Nameless", "Unwritten", "Shifting", "Ageless", "Deep", "Faceless"],
    titleB: ["Echo", "Depth", "Hour", "Choir", "Mirror", "Wound", "Door", "Throne", "Name", "Vault"],
    openers: [
      "Deeper than any map,", "Where the floors stop obeying time,", "In the halls that were never built,",
      "Past the last honest door,", "Where the descent turns strange,", "In the age the surface forgot,",
      "Where memory outlives its owner,", "Beneath the reach of any firelight,", "In the depth that answers back,",
      "Where the Vault dreams without a Keeper,", "Past the point of no returning,", "In the dark that keeps its own hours,",
    ],
    cores: [
      "the Forgotten drift — no faces, no names, only echoes of who they were",
      "some rooms have never existed until a seeker opens the door",
      "the same hall loops forever, and the map lies to keep you walking",
      "the deep relics hold a will older than the kingdoms that craved them",
      "adventurers who went too far became the horrors that others now flee",
      "time pools like water, and an hour below is an age above",
      "the Vault shows each seeker a memory that is almost their own",
      "the throne at the dark's heart sits empty, and the emptiness watches",
      "creatures once imprisoned have evolved into something the wards never named",
      "every floor is another page in the memory of a universe that will not forget",
      "the descent has no bottom, and the ascent has no top",
      "the oldest Forgotten remember being heroes, and hate the ones still living",
      "mirrors here show the face you will wear when the dark finally keeps you",
      "the deep sings in a choir of every voice it has ever swallowed",
    ],
    closes: [
      "and the dark did not blink.", "— and it remembered you.", "so the map lied on.",
      "and something older stirred.", "and the descent went on.", "— a truth best left unread.",
      "and the hour pooled and held.", "and the throne stayed empty, and watched.",
      "so the Forgotten drifted nearer.", "and no firelight followed.",
    ],
  },
  {
    name: "Restoration", color: "#ffe8a3",
    codexA: ["The Helix", "The Mending", "The Keeper's Return", "The Whole", "The Restored", "The Last Door", "The Gathered Song", "The World Remade", "The Final", "The Dawn"],
    codexB: ["Awakening", "Mending", "Return", "Choir", "Vow", "Dawn", "Restoration", "Crown", "Hour", "Light"],
    titleA: ["Mending", "Whole", "Returning", "Restored", "Final", "Dawning", "Gathered", "Remade", "Last", "Radiant"],
    titleB: ["Helix", "Dawn", "Return", "Choir", "Crown", "Vow", "Light", "Hour", "Song", "Mending"],
    openers: [
      "When the memories were gathered whole,", "As the Helix began to shine,", "When the last codex was closed,",
      "As the world remembered how to heal,", "When the Keeper's oath was taken up again,", "At the turning of the long dark,",
      "As the seeker became the mending,", "When the collection was made whole,", "At the dawn the founders foresaw,",
      "When memory forgave its keeper,", "As the Helix drew the story tight,", "When the Vault chose to wake kindly,",
    ],
    cores: [
      "each recovered memory lit another rung of the great restoring Helix",
      "the world began to knit itself from the story the seekers carried up",
      "the empty throne stirred, and something chose at last to keep the oath",
      "the Forgotten were remembered by name, and were forgotten no longer",
      "the relics returned their memories willingly, and grew light",
      "Eden's makers stepped at last out of their long anonymity",
      "the Fracture closed the way a wound closes over a seed",
      "the descent reversed, and the deep gave back everything it had kept",
      "the seeker who gathered the whole story became its final page",
      "the balance the Architects struck was struck again, gentler this time",
      "the Helix turned once, and a thousand years of dark exhaled",
      "the world woke remembering, and chose to remember kindly",
      "the last door opened outward, onto morning",
      "the collection was made whole, and mercy learned a softer name",
    ],
    closes: [
      "and Relikara was whole.", "— and the world was restored.", "and the dark gave up its dead gently.",
      "so the story closed, and began.", "and morning came to the deep.", "— the oath, at last, fulfilled.",
      "and nothing was lost.", "and the Helix shone entire.", "so the world remembered itself.",
      "— dawn, unbroken.",
    ],
  },
];

// ---- Generate the library ----
const CODICES = [];
const MEMORY_BY_ID = {};
for (let c = 1; c <= CODEX_COUNT; c++) {
  const tierIdx = Math.min(TIERS.length - 1, Math.floor((c - 1) / TIER_SIZE));
  const T = TIERS[tierIdx];
  const cs = hash(c, 777);
  const title = `${pick(T.codexA, cs)} ${pick(T.codexB, cs >>> 7)}`;
  const count = 6 + (cs % 4);                    // 6..9 memories
  const memories = [];
  for (let m = 1; m <= count; m++) {
    const s = hash(c, m);
    const text = `${pick(T.openers, s)} ${pick(T.cores, s >>> 5)}${pick(T.closes, s >>> 11)}`;
    const mtitle = `${pick(T.titleA, s >>> 3)} ${pick(T.titleB, s >>> 17)}`;
    const mem = { id: `m${c}-${m}`, codexId: c, idx: m, title: mtitle, text };
    memories.push(mem);
    MEMORY_BY_ID[mem.id] = mem;
  }
  CODICES.push({ id: c, tier: tierIdx + 1, tierName: T.name, color: T.color, title, memories });
}

export { CODICES, MEMORY_BY_ID };
export const TOTAL_MEMORIES = CODICES.reduce((n, c) => n + c.memories.length, 0);

// ---- Queries (collected = meta.codex, a { memId: true } map) ----
export const getCodex = (id) => CODICES[id - 1] || null;
export const getMemory = (id) => MEMORY_BY_ID[id] || null;
export function codexProgress(codexId, collected = {}) {
  const cx = getCodex(codexId); if (!cx) return { have: 0, total: 0 };
  let have = 0; for (const m of cx.memories) if (collected[m.id]) have++;
  return { have, total: cx.memories.length };
}
export function codexComplete(codexId, collected = {}) {
  const p = codexProgress(codexId, collected);
  return p.total > 0 && p.have >= p.total;
}
export function completedCodexCount(collected = {}) {
  let n = 0; for (let c = 1; c <= CODEX_COUNT; c++) if (codexComplete(c, collected)) n++;
  return n;
}
export function totalCollected(collected = {}) {
  let n = 0; for (const id in collected) if (collected[id]) n++;
  return n;
}

// ---- Depth unlock: how many codices can drop memories, given deepest floor reached ----
export function unlockedCeiling(deepest = 0) {
  return Math.max(3, Math.min(CODEX_COUNT, Math.round((deepest || 0) * 2.2) + 3));
}

/**
 * Pick a memory to drop, gated by depth. Prefers an uncollected memory from the
 * lowest incomplete codex within the unlocked band (keeps the story moving
 * forward), with some spread; falls back to any memory in band (a duplicate).
 */
export function rollMemory(collected = {}, deepest = 0) {
  const ceiling = unlockedCeiling(deepest);
  const uncollected = [];
  for (let c = 1; c <= ceiling; c++) {
    for (const m of getCodex(c).memories) if (!collected[m.id]) uncollected.push(m);
  }
  if (uncollected.length) {
    // bias toward the front (earlier codices) so the tale unfolds in order
    const r = Math.random() ** 1.6;                 // skew low
    return uncollected[Math.floor(r * uncollected.length)];
  }
  // everything in-band collected → hand back a duplicate from the band
  const c = 1 + Math.floor(Math.random() * ceiling);
  const mem = getCodex(c).memories;
  return mem[Math.floor(Math.random() * mem.length)];
}

const SCROLL_MODELS = ["relic/scroll_sealed", "relic/scroll_metal", "relic/scroll_basic"];
/** Build a droppable Memory item (kept as kind "fragment" for existing pipes). */
export function makeMemoryItem(mem) {
  return {
    kind: "fragment", fragId: mem.id, codexId: mem.codexId,
    name: `Memory: ${mem.title}`, memText: mem.text,
    model: SCROLL_MODELS[Math.floor(Math.random() * SCROLL_MODELS.length)],
    color: "#c9a24b", rarity: RARITY[2],
    flavor: "A fragment of the Vault's memory. Offer it at the Helix.",
  };
}
